import { router } from 'expo-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, TextInput, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { AppText } from '@/components/ui/app-text';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ScreenHeader } from '@/components/ui/screen-header';
import { useAuthStore } from '@/store/auth-store';
import { colors, fonts, radius, spacing } from '@/theme/tokens';

/**
 * Login step 1 — mock: logowanie_telefon/screen.png. Phone-only login (no
 * passwords, MVP auth decision): we send an SMS code to this number. The guest
 * escape link is prominent on purpose — login is optional, never a wall.
 */
export default function AuthPhoneScreen() {
  const { t } = useTranslation();
  const requestCode = useAuthStore((s) => s.requestCode);

  const [digits, setDigits] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  // Referral code is opt-in and collapsed by default — most logins have none.
  const [showReferral, setShowReferral] = useState(false);
  const [referralCode, setReferralCode] = useState('');

  const formatted = digits.replace(/(\d{3})(?=\d)/g, '$1 ');
  const valid = digits.length === 9;

  const onSend = async () => {
    setSending(true);
    setError(null);
    const result = await requestCode(`+48${digits}`, referralCode || undefined);
    setSending(false);
    if (result.ok) {
      router.push('/auth/code');
    } else {
      setError(result.message ?? t('auth.genericError'));
    }
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}>
        <ScreenHeader title={t('auth.loginTitle')} />

        <AppText variant="headlineLg" color={colors.navy}>
          {t('auth.phoneTitle')}
        </AppText>
        <AppText variant="bodyLg">{t('auth.phoneSubtitle')}</AppText>

        <View style={{ gap: spacing.base }}>
          <AppText variant="labelMd" color={colors.primary}>
            {t('auth.phoneLabel')}
          </AppText>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              backgroundColor: colors.primaryTile,
              borderRadius: radius.card,
              paddingHorizontal: spacing.md,
              minHeight: 64,
              gap: spacing.sm,
            }}
          >
            <AppText variant="headlineSm" color={colors.navy}>
              +48
            </AppText>
            <TextInput
              value={formatted}
              onChangeText={(text) => setDigits(text.replace(/\D/g, '').slice(0, 9))}
              placeholder="___ ___ ___"
              placeholderTextColor={colors.outlineVariant}
              keyboardType="phone-pad"
              autoFocus
              style={{
                flex: 1,
                fontFamily: fonts.semiBold,
                fontSize: 22,
                color: colors.navy,
                paddingVertical: spacing.sm,
              }}
            />
            <Ionicons name="call-outline" size={22} color={colors.primary} />
          </View>
        </View>

        {/* "Masz kod polecenia?" — friend's code = 50 zł voucher for both sides.
            Code-based attribution on purpose: nobody types anyone else's phone. */}
        {showReferral ? (
          <View style={{ gap: spacing.base }}>
            <AppText variant="labelMd" color={colors.primary}>
              {t('auth.referralLabel')}
            </AppText>
            <TextInput
              value={referralCode}
              onChangeText={(text) => setReferralCode(text.toUpperCase().slice(0, 14))}
              placeholder="POL-___-___"
              placeholderTextColor={colors.outlineVariant}
              autoCapitalize="characters"
              autoCorrect={false}
              style={{
                backgroundColor: colors.primaryTile,
                borderRadius: radius.card,
                paddingHorizontal: spacing.md,
                minHeight: 56,
                fontFamily: fonts.semiBold,
                fontSize: 18,
                color: colors.navy,
              }}
            />
            <AppText variant="labelMd">{t('auth.referralHint')}</AppText>
          </View>
        ) : (
          <Pressable
            accessibilityRole="button"
            onPress={() => setShowReferral(true)}
            style={{ minHeight: 44, justifyContent: 'center' }}
          >
            <AppText variant="labelLg" color={colors.primary}>
              {t('auth.referralToggle')}
            </AppText>
          </Pressable>
        )}

        {error && (
          <AppText variant="bodyMd" color={colors.warning}>
            {error}
          </AppText>
        )}

        <PrimaryButton
          label={t('auth.sendCode')}
          disabled={!valid || sending}
          onPress={onSend}
        />

        <Pressable accessibilityRole="button" onPress={() => router.back()} style={{ minHeight: 44, justifyContent: 'center' }}>
          <AppText variant="labelLg" color={colors.primary} style={{ textAlign: 'center' }}>
            {t('auth.stayGuest')}
          </AppText>
        </Pressable>

        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.xs, marginTop: spacing.md }}>
          <Ionicons name="shield-checkmark-outline" size={16} color={colors.outlineVariant} />
          <AppText variant="labelMd">{t('auth.privacyFooter')}</AppText>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}
